/**
 * The CRM tab's small helpers, copied from the design's salesVals closures (L20026-21675)
 * so the strings match: title case, stage caps, "3d ago", money in k, and the button and
 * chip styles the CRM markup reads.
 */
import type { CrmMember } from '@aithora/shared';

/**
 * design title() / tc(): a name that arrives in capitals (the CMS catalog) gets every word
 * capitalised; a name typed in mixed case is kept as typed.
 */
export const title = (x: string | null | undefined) => {
  const s = String(x ?? '');
  return s === s.toUpperCase() ? s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase()) : s;
};
/** design cap(): "LEAD" or "Lead" to "Lead". */
export const cap = (x: string | null | undefined) => {
  const s = String(x ?? '');
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};
/** The stage key the design uses (LEAD, QUALIFIED, PILOT SITE ...). */
export const stageKey = (name: string | null | undefined) => String(name ?? '').toUpperCase();

/** design ago(): "just now", "12m ago", "3h ago", "2d ago". */
export function ago(iso: string | number | null | undefined, now = Date.now()): string {
  if (iso == null) return '';
  const t = typeof iso === 'number' ? iso : Date.parse(iso);
  const m = Math.round((now - t) / 60000);
  return m < 1 ? 'just now' : m < 60 ? m + 'm ago' : m < 1440 ? Math.round(m / 60) + 'h ago' : Math.round(m / 1440) + 'd ago';
}

/** Dollars a year as the design's k figures ("$471k/yr"). */
export const kYr = (dollars: number) => '$' + Math.round(dollars / 1000) + 'k/yr';
export const k = (dollars: number) => '$' + Math.round(dollars / 1000) + 'k';
/** Thousands with the tenth kept when there is one (design '$' + openK + 'k': "$470.5k", "$159k"). */
export const k10 = (dollars: number) => '$' + Math.round(dollars / 100) / 10 + 'k';
/** design fmtK(): "$470.5k/yr". */
export const k1Yr = (dollars: number) => '$' + (dollars / 1000).toFixed(1) + 'k/yr';

/** A calendar date (YYYY-MM-DD) in the viewer's zone. */
export function localDay(iso: string | number | Date, tz?: string): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** A person's first name for "3d ago · Andrew" (design ME = 'Andrew'). */
export function firstName(members: CrmMember[] | undefined, id: string | null | undefined): string {
  if (!id) return 'Unassigned';
  const m = members?.find((x) => x.id === id);
  const n = m?.name ?? m?.email ?? null;
  return n ? n.split(/[\s@]/)[0] : 'A teammate';
}

// ── styles (design salesVals L20031-20042, L21048-21050, L21284-21285) ─────────
export const MONO = 'font-variant-numeric:tabular-nums;';
export const chip = (fg: string, bg: string) =>
  'font-size:14px;font-weight:700;letter-spacing:.07em;border-radius:5px;padding:2px 7px;flex:0 0 auto;white-space:nowrap;color:' + fg + ';background:' + bg + ';';
export const btn = (primary: boolean) =>
  'display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;height:30px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (primary ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);');
export const sub = (on: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:#FCFAF4;color:#22272E;border:1px solid rgba(34,39,46,.16);' : 'background:transparent;color:#54606C;');
export const fig = (c: string, px?: number) => 'font-size:' + (px || 26) + 'px;font-weight:700;line-height:1;letter-spacing:-.015em;color:' + c + ';';
export const cell = (align: string, weight?: number, color?: string) =>
  'font-size:16px;' + (align ? 'text-align:' + align + ';' : '') + 'font-weight:' + (weight || 400) + ';color:' + (color || '#22272E') + ';overflow-wrap:break-word;';
export const capt = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
/** My day row buttons (L21048-21050). */
export const ghostToday =
  'display:inline-flex;align-items:center;justify-content:center;height:28px;padding:0 10px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;color:#22272E;border:1px solid rgba(34,39,46,.2);background:transparent;';
export const primToday =
  'display:inline-flex;align-items:center;justify-content:center;height:28px;padding:0 11px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#fff;background:#2F5C97;border:1px solid #2F5C97;';
export const offToday =
  'display:inline-flex;align-items:center;justify-content:center;height:28px;padding:0 11px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#54606C;background:rgba(34,39,46,.07);border:1px solid rgba(34,39,46,.12);cursor:not-allowed;';
/** Workspace buttons (L21284-21285). */
export const ghost =
  'display:inline-flex;align-items:center;justify-content:center;height:28px;padding:0 10px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;color:#22272E;border:1px solid rgba(34,39,46,.2);';
export const prim =
  'display:inline-flex;align-items:center;justify-content:center;height:28px;padding:0 11px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#fff;background:#2F5C97;';
/** The deal panel's primary (design L21049): the same button with its own 1px border. */
export const primDeal = prim + 'border:1px solid #2F5C97;';
/** A write the caller may not make: the same button (the design shows no disabled look), the reason as its title, and no action. */
export const disabledWrite = 'cursor:not-allowed;';

/** Next step per stage (design NEXT, L21030-21031): the playbook's move for each stage. */
export const NEXT: Record<string, string> = {
  LEAD: 'Book a discovery call',
  QUALIFIED: 'Send the one-pager and propose a pilot',
  PILOT: 'Agree success metrics in writing',
  CONTRACT: 'Send the founding agreement',
  LIVE: 'Hand over to onboarding',
  INFOSEC: 'Send the security packet',
  LEGAL: 'Redline the agreement',
  PROCUREMENT: 'Vendor forms and insurance certificate',
  'PILOT SITE': 'Pick the pilot building',
  'MULTI-SITE PILOT': 'Agree the rollout sites',
  'CORPORATE CASE': 'Present the corporate case',
  EXPANSION: 'Plan the next buildings',
};

/** Stage dot colours (design STAGES_A / STAGES_B, L20337-20344). */
export const STAGE_COLOR: Record<string, string> = {
  LEAD: '#2F5C97', QUALIFIED: '#2F5C97', PILOT: '#5B6B7C', CONTRACT: '#264C7E', LIVE: '#1F6B52', LOST: '#B3261E',
  INFOSEC: '#5B6B7C', LEGAL: '#5B6B7C', PROCUREMENT: '#5B6B7C', 'PILOT SITE': '#264C7E', 'MULTI-SITE PILOT': '#264C7E',
  'CORPORATE CASE': '#264C7E', EXPANSION: '#1F6B52',
};

/** The Lost reasons the server accepts (src/crm/sales-rules.ts LOST_REASONS). */
export const LOST_REASONS = ['No budget', 'Chose another vendor', 'Not the right time', 'No response'] as const;

/** mailto with partner@ on CC (one reply address for the facility) and deals@ on BCC (filing). */
export const mailHref = (subject: string, body?: string) =>
  'mailto:?cc=partner@aithoracare.com&bcc=deals@aithoracare.com&subject=' + encodeURIComponent(subject) + (body != null ? '&body=' + encodeURIComponent(body) : '');
